import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'theme';

const ThemeContext = createContext<{ theme: Theme; toggle: () => void }>({ theme: 'light', toggle: () => {} });

export const useTheme = () => useContext(ThemeContext);

/**
 * Applies the theme to the document.
 *
 * `data-theme` on <html> drives our own CSS custom properties; `awsui-dark-mode`
 * on <body> is what Cloudscape reads. The inline script at the top of
 * public/index.html performs this same pair of writes synchronously during parse,
 * before the first paint. Keep the two in step.
 */
function applyTheme(theme: Theme): void {
    document.documentElement.setAttribute('data-theme', theme);
    document.body.classList.toggle('awsui-dark-mode', theme === 'dark');
}

/**
 * Reads the theme the pre-paint script already resolved and applied.
 *
 * Reading the attribute rather than localStorage keeps React's first render in
 * agreement with what is on screen, which matters because prerendered pages are
 * hydrated: deriving the theme independently here risks rendering markup for a
 * theme the document is not actually in.
 */
function getInitialTheme(): Theme {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState<Theme>(getInitialTheme);

    useEffect(() => {
        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch {
            // localStorage unavailable (private browsing, blocked cookies) — the
            // theme still applies for this page, it just will not persist.
        }
        applyTheme(theme);
    }, [theme]);

    const toggle = () => setTheme(t => t === 'light' ? 'dark' : 'light');

    return <ThemeContext.Provider value={{ theme, toggle }}>{children}</ThemeContext.Provider>;
};
